import { Injectable } from '@angular/core';
import {
  CoreQuestionsFactoryService,
  CountriesService,
  DistributorsService,
  LocalesService,
  ProfileService,
  UtilsQuestionsService
} from 'core/services';
import {
  QuestionBase,
  DropdownQuestion,
  EntityQuestion,
  HiddenQuestion,
  TextboxQuestion,
  QuestionGroup,
  CheckboxQuestion
} from 'core/components/forms';
import { Agent, Role } from '@models/agent';
import { TranslateService } from '@ngx-translate/core';
import { Client } from 'core/models';

@Injectable()
export class AgentsQuestionsService {
  private userRole: Role;
  private loggedUser: any;
  private questions: QuestionBase<any>[];
  private dataQuestionGroup: QuestionGroup;
  private notificationsQuestionGroup: QuestionGroup;

  constructor(
    private factory: CoreQuestionsFactoryService,
    private countries: CountriesService,
    private distributors: DistributorsService,
    private locales: LocalesService,
    private profileService: ProfileService,
    private translate: TranslateService,
    private utilsQuestionsService: UtilsQuestionsService
  ) {
    profileService.getLoggedUser$().subscribe((loggedUser) => {
      this.loggedUser = loggedUser;
      this.userRole = loggedUser.roleOptions;
    });
  }

  getQuestions(agent?: Agent, client?: Client) {
    const questions = [this.getDataQuestions(agent, client), this.getNotificationsQuestionGroup(agent)];
    this.questions = questions.filter((q) => !!q);
    return this.questions;
  }

  getDataQuestions(agent?: Agent, client?: Client): QuestionGroup {
    let countryId = agent ? agent.countryId : client ? client.countryId : 'ESP';
    const timezone = this.countries.getDefaultTimezone(countryId);
    const rolesOptions = this.profileService.getRolesOptions(true, false, true, agent?.role);

    const dataQuestions = [
      this.getRoleQuestion(agent, rolesOptions, client),
      this.getDistributorQuestion(agent, agent ? agent.role : null),
      this.getClientsQuestion(agent, agent ? agent.role : null, client),
      new TextboxQuestion({
        key: 'email',
        label: 'nouns.email',
        class: 'col-1',
        type: 'email',
        editable: !agent,
        value: agent ? agent.email : null,
        validators: { required: !agent, mail: true },
        order: 1
      }),
      new TextboxQuestion({
        key: 'name',
        label: 'agents.header.name',
        class: 'col-2',
        editable: this.canEditAgent(agent?.role),
        value: agent ? agent.name : null,
        validators: { required: true },
        order: 2
      }),
      new TextboxQuestion({
        key: 'surname',
        label: 'nouns.surname',
        class: 'col-2',
        editable: this.canEditAgent(agent?.role),
        value: agent ? agent.surname : null,
        order: 3
      }),
      new TextboxQuestion({
        key: 'pid',
        label: 'nouns.pid',
        class: 'col-2-offset',
        editable: this.canEditAgent(agent?.role),
        value: agent ? agent.pid : null,
        order: 4
      }),
      new DropdownQuestion({
        key: 'countryId',
        label: 'nouns.country',
        placeholder: 'actions.selectCountry',
        class: 'col-2',
        editable: this.canEditAgent(agent?.role),
        value: countryId,
        optionsObservable: this.countries.getOptionsObservable(),
        validators: { required: true },
        eventHandlers: {
          onChange: (value) => {
            countryId = value;
            this.updateTimezoneQuestion(countryId);
          }
        },
        order: 7
      }),
      new DropdownQuestion({
        key: 'timezone',
        class: 'col-2',
        label: 'nouns.timezone',
        editable: this.canEditAgent(agent?.role),
        options: this.countries.getTimezoneOptions(countryId),
        value: agent ? agent.timezone : timezone,
        validators: { required: true },
        order: 8
      }),
      new DropdownQuestion({
        key: 'lang',
        label: 'nouns.language',
        placeholder: 'actions.selectLanguage',
        class: 'col-2-offset',
        editable: this.canEditAgent(agent?.role),
        options: this.locales.getDefaultOptions(),
        value: agent ? agent.lang : null,
        validators: { required: true },
        order: 6
      }),
      new TextboxQuestion({
        key: 'phone',
        label: 'nouns.phone',
        class: 'col-2-offset',
        editable: this.canEditAgent(agent?.role),
        value: agent ? agent.phone : null,
        order: 5
      }),

      new CheckboxQuestion({
        key: 'blocked',
        class: 'col-2-offset',
        label: 'nouns.agentBlocked',
        editable: this.canEditAgent(agent?.role) || this.profileService.canExecute('agents.blockAccount'),
        value: agent ? agent.blocked : false,
        order: 13
      })
    ];

    if (
      agent &&
      (this.userRole.group === 'cs' || this.userRole.scope === 'root') &&
      (agent.role === 'clientAdmin' || agent.role === 'dachAdmin')
    ) {
      dataQuestions.push(
        new QuestionGroup({
          key: 'ownerConfig',
          childQuestions: [
            new CheckboxQuestion({
              key: 'owner',
              class: `col-1`,
              label: `${agent.ownerConfig && agent.ownerConfig.automatic ? 'nouns.automaticOwner' : 'nouns.owner'}`,
              editable: this.canEditAgent(agent?.role),
              value: agent.ownerConfig ? agent.ownerConfig.owner : false,
              eventHandlers: {
                onChange: () => {
                  this.updateOwnerQuestion(agent.role);
                }
              }
            }),
            new HiddenQuestion({
              key: 'automatic',
              value: agent.ownerConfig ? agent.ownerConfig.automatic : false
            })
          ],
          order: 12
        })
      );
    }

    if (
      ['distributorManager', 'expert'].includes(this.userRole.name) ||
      this.userRole.group === 'cs' ||
      this.userRole.scope === 'root'
    ) {
      dataQuestions.push(
        new CheckboxQuestion({
          key: 'isCreator',
          class: 'col-2-offset',
          label: 'nouns.accessEditor',
          editable: agent?.role !== 'creator' && this.canEditAgent(agent?.role),
          value: agent ? agent.isCreator : false,
          order: 14
        })
      );
    }

    this.dataQuestionGroup = new QuestionGroup({
      title: 'agents.info',
      isSection: true,
      childQuestions: dataQuestions.sort((a, b) => a.order - b.order)
    });

    if (agent) {
      this.updateClientIdQuestion(agent.role, agent, client);
    }
    return this.dataQuestionGroup;
  }

  getNotificationsQuestionGroup(agent?: Agent, role?: string) {
    const agentRole = agent ? agent.role : role;
    if (!['clientAdmin', 'dachAdmin', 'hr', 'distributor', 'expert'].includes(agentRole)) {
      return null;
    }

    const notificationsCfg = agent
      ? agent.notificationsCfg
      : {
          na2: {
            enabled: true,
            weekPeriodicity: 1
          }
        };

    this.notificationsQuestionGroup = new QuestionGroup({
      title: 'agents.notifications',
      isSection: true,
      childQuestions: [
        new QuestionGroup({
          key: 'notificationsCfg',
          childQuestions: [
            new QuestionGroup({
              key: 'na2',
              class: 'col-1',
              childQuestions: [
                new CheckboxQuestion({
                  key: 'enabled',
                  tooltipKey: 'notificationsCfg.na2.enabled',
                  class: 'col-1',
                  label: 'agents.mailResumeActivity',
                  editable: this.canEditAgent(agent?.role),
                  value: notificationsCfg.na2.enabled,
                  eventHandlers: {
                    onChange: (value) => {
                      this.setFormQuestions('weekPeriodicity', value, this.notificationsQuestionGroup.childQuestions);
                    }
                  }
                }),
                this.getNa2WeekPeriodicityQuestion(notificationsCfg)
              ]
            })
          ]
        })
      ]
    });
    return this.notificationsQuestionGroup;
  }

  getNa2WeekPeriodicityQuestion(notificationsCfg: any) {
    return new DropdownQuestion({
      key: 'weekPeriodicity',
      tooltipKey: 'notificationsCfg.na2.weekPeriodicity',
      label: 'agents.mailFrequencyResumeActivity',
      class: 'col-2-offset',
      disabled: !notificationsCfg.na2.enabled,
      value: notificationsCfg.na2.weekPeriodicity,
      options: [
        { key: 1, value: this.translate.instant('calendar.weeks.1') },
        { key: 2, value: this.translate.instant('calendar.weeks.2') },
        { key: 4, value: this.translate.instant('calendar.weeks.4') }
      ]
    });
  }

  getClientsQuestion(agent, role, client) {
    let clientsQuestion;
    if (!client && role && role === 'creator' && ['distributor', 'expert'].includes(this.userRole.name)) {
      clientsQuestion = new HiddenQuestion({
        key: 'clientIds',
        validators: { required: false },
        value: agent ? agent.clientIds : [this.loggedUser.clientId],
        order: 1
      });
    } else if (
      (client && !['distributor', 'expert'].includes(role)) ||
      (role && this.profileService.getRoleData(role)?.scope === 'client')
    ) {
      clientsQuestion = this.factory.getClientQuestion({
        key: 'clientIds',
        label: 'nouns.client',
        title: 'actions.addTags',
        placeholder: 'actions.addTags',
        value: agent ? agent.clientIds : client ? [client.id] : null,
        editable: this.canEditAgent(agent?.role),
        entityOptions: {
          multiple: true,
          preload: agent ? agent.clientIds : client ? [client.id] : this.userRole.scope === 'client'
        },
        validators: { required: true },
        order: 1
      });
    } else {
      clientsQuestion = new HiddenQuestion({
        key: 'clientIds',
        validators: { required: false },
        value: agent ? agent.clientIds : null,
        order: 1
      });
    }
    return clientsQuestion;
  }

  getDistributorQuestion(agent, role) {
    let distributorQuestion;
    if (['expert', 'distributor'].includes(this.userRole.name)) {
      distributorQuestion = new HiddenQuestion({
        key: 'distributorId',
        validators: { required: false },
        value: agent ? agent.distributorId : this.loggedUser.distributorId,
        order: 1
      });
    } else if (role && (role === 'distributor' || role === 'expert')) {
      distributorQuestion = new EntityQuestion({
        key: 'distributorId',
        label: role === 'distributor' ? 'distributors.distributor' : 'distributors.expert',
        dataProvider: this.distributors,
        editable: this.canEditAgent(agent?.role),
        value: agent && agent.distributorId ? agent.distributorId : null,
        entityOptions: {
          valueField: 'id',
          preload: agent ? [agent.distributorId] : false,
          filters: { type: role }
        },
        validators: { required: true },
        order: 1
      });
    } else {
      distributorQuestion = new HiddenQuestion({
        key: 'distributorId',
        validators: { required: false },
        value: agent && agent.distributorId ? agent.distributorId : null,
        order: 1
      });
    }
    return distributorQuestion;
  }

  private updateTimezoneQuestion(countryId) {
    const index = this.dataQuestionGroup.childQuestions.findIndex((question) => question.key === 'timezone');
    const questionOptions = Object.assign(this.dataQuestionGroup.childQuestions[index], {
      options: this.countries.getTimezoneOptions(countryId),
      value: this.countries.getDefaultTimezone(countryId)
    });
    this.dataQuestionGroup.childQuestions.splice(index, 1, new DropdownQuestion(questionOptions));
  }

  private updateClientIdQuestion(selectedRole, agent, client) {
    const clientsIndex = this.dataQuestionGroup.childQuestions.findIndex((question) => question.key === 'clientIds');
    const distributorIndex = this.dataQuestionGroup.childQuestions.findIndex(
      (question) => question.key === 'distributorId'
    );
    this.dataQuestionGroup.childQuestions.splice(distributorIndex, 1, this.getDistributorQuestion(agent, selectedRole));
    this.dataQuestionGroup.childQuestions.splice(clientsIndex, 1, this.getClientsQuestion(agent, selectedRole, client));
  }

  private updateOwnerQuestion(selectedRole) {
    const ownerIndex = this.dataQuestionGroup.childQuestions.findIndex((question) => question.key === 'ownerConfig');

    if (
      (this.userRole.group === 'cs' || this.userRole.scope === 'root') &&
      (selectedRole === 'clientAdmin' || selectedRole === 'dachAdmin')
    ) {
      this.dataQuestionGroup.childQuestions.splice(
        ownerIndex,
        ownerIndex === -1 ? 0 : 1,
        new QuestionGroup({
          key: 'ownerConfig',
          childQuestions: [
            new CheckboxQuestion({
              key: 'owner',
              class: 'col-1',
              label: 'nouns.owner',
              editable: this.canEditAgent(selectedRole),
              value: false
            }),
            new HiddenQuestion({
              key: 'automatic',
              value: false
            })
          ],
          order: 12
        })
      );
    }
  }

  private updateIsCreatorQuestion(selectedRole, agent?) {
    const isCreatorIndex = this.dataQuestionGroup.childQuestions.findIndex((question) => question.key === 'isCreator');
    this.questions.splice(1, 1, this.getNotificationsQuestionGroup(agent, selectedRole));
    if (
      ['distributorManager', 'expert'].includes(this.userRole.name) ||
      this.userRole.group === 'cs' ||
      this.userRole.scope === 'root'
    ) {
      this.dataQuestionGroup.childQuestions.splice(
        isCreatorIndex,
        isCreatorIndex === -1 ? 0 : 1,
        new CheckboxQuestion({
          key: 'isCreator',
          class: 'col-1',
          label: 'nouns.accessEditor',
          editable: this.canEditAgent(agent?.role),
          value: selectedRole === 'creator',
          order: 14
        })
      );
    }
  }

  private getRoleQuestion(agent, rolesOptions, client): DropdownQuestion {
    return new DropdownQuestion({
      key: 'role',
      label: 'nouns.role',
      class: 'col-2-offset',
      value: agent ? agent.role : null,
      placeholder: 'actions.selectRole',
      options: agent?.role !== 'superAdmin' ? rolesOptions : [{ key: 'superAdmin', value: agent.fullName }],
      editable: !agent,
      validators: { required: !agent },
      order: 0,
      eventHandlers: {
        onChange: (value) => {
          this.updateClientIdQuestion(value, agent, client);
          this.updateIsCreatorQuestion(value, agent);
          this.updateOwnerQuestion(value);
        }
      }
    });
  }

  /**
   * Set the boolean values for the questions of the form
   * @param questionKey string
   * @param value boolean
   * @param questions list of any
   * @private
   */
  private setFormQuestions(questionKey: string, value: boolean, questions: any[]) {
    const foundQuestion = this.utilsQuestionsService.findQuestion(questionKey, questions);
    if (foundQuestion) {
      foundQuestion.disabled = !value;
    }
  }

  /**
   * Check if the agent role can edit the fields
   * @param agentRole
   * @private
   */
  private canEditAgent(agentRole: string): boolean {
    if (!agentRole) return true;
    return this.userRole.priority < this.profileService.getRoleData(agentRole)?.priority;
  }
}
